<template>
  <div class="home">
  <!-- Hero Section -->
  <section class="hero">
    <div class="hero-content">
      <div class="hero-image">
        <img src="@/assets/products/ion-both-hero.jpg" alt="UV Effects analog guitar pedals with NOS germanium diodes and vintage silicon transistors - handmade in Colorado" />
      </div>

      <div class="hero-text">
        <h1>Vintage-inspired tone, hand-wired in Colorado.</h1>
        <p>UV Effects crafts analog gain pedals using only the finest New Old Stock (NOS) silicon and germanium components. Every unit is hand-built with premium hardware to deliver responsive touch and transparent tone.</p>
        <router-link to="/shop/" class="shop-button">Browse Pedals</router-link>
      </div>
      
    </div>
  </section>

    <!-- Featured Pedal: PION -->
    <section class="featured-pedal-section">
      <div class="container">
        <div class="featured-pedal-content">
          <div class="featured-pedal-image">
            <router-link to="/pedals/pion/">
              <img src="@/assets/products/pion1.jpg" alt="PION Fuzz + Overdrive + Boost - the ultimate three-stage gain pedal" />
            </router-link>
          </div>
          <div class="featured-pedal-info">
            <p class="featured-label">Featured Pedal</p>
            <h2>PION</h2>
            <p class="featured-subtitle">Fuzz + Overdrive + Boost</p>
            <p class="featured-description">
              The ultimate compact three-stage gain pedal. A vintage-voiced fuzz, transparent amp-like overdrive, and tube-feeling boost in one enclosure.
            </p>
            <p class="featured-price">$175</p>
            <router-link to="/pedals/pion/" class="featured-button">View PION</router-link>
          </div>
        </div>
      </div>
    </section>

    <!-- Free Clipper Section -->
    <section class="keychain-section">
      <div class="container">
        <div class="keychain-content">
          <div class="keychain-image">
            <router-link to="/accessories/clipper/">
              <img src="@/assets/products/keychainpile.jpg" alt="Germanium Clippers - free with every pedal purchase" />
            </router-link>
          </div>
          <div class="keychain-info">
            <p class="keychain-label">Included Free</p>
            <h2>Germanium Clipper</h2>
            <p class="keychain-description">
              Every pedal ships with a free Germanium Clipper: a fully functional passive clipping circuit you can clip to your gig bag or strap. Carry the mythical diodes of vintage tone wherever you go, and clip it into your signal chain in an emergency for real harmonic saturation.
            </p>
            <router-link to="/accessories/clipper/" class="keychain-button">Learn More</router-link>
          </div>
        </div>
      </div>
    </section>

    <!-- What Makes UV Effects Different -->
    <section class="features">
      <div class="container">
        <h2 class="section-title">What Makes UV Effects Different</h2>
        
        <div class="features-grid">
          <div class="feature">
            <h3>Mojo NOS Parts</h3>
            <p>
              Every UV Effects pedal is built with hand selected New Old Stock silicon and germanium components, including transistors, JFETs, and diodes. These parts are carefully tested and chosen for tone, noise performance, and dynamic response, delivering the vintage inspired character that modern components cannot replicate.
            </p>
          </div>
          
          <div class="feature">
            <h3>Multi-Drive Mastery</h3>
            <p>
              UV Effects specializes in stacking gain stages, from single circuit designs to complex multi drive pedals. Each stage is carefully voiced and tuned to work together, allowing you to combine fuzz, overdrive, and boost in a controlled and musical way. The result is a wide range of usable tones, from subtle sweetening to full saturation, without losing clarity or touch response.
            </p>
          </div>
          
          <div class="feature">
            <h3>Premium Build Standards</h3>
            <p>
              Built with premium hardware and rugged enclosures, our pedals are designed for tour-grade reliability. We use only through-hole components, and every circuit is tuned with fixed-value resistors for permanent precision. We never rely on flimsy trimpots. No shortcuts, no compromises. Just high-quality, hand-wired craftsmanship.
            </p>
          </div>
          
          <div class="feature">
            <h3>Tone-First Aesthetic</h3>
            <p>
              We remove all non essential elements. There is no paint, no graphics, and no labels. Each pedal uses a raw enclosure with high quality controls, allowing the circuit to take center stage. This rugged, industrial design encourages you to rely on feel and sound, not visuals, to find the sweet spot.
            </p>
          </div>
        </div>
      </div>
    </section>

    <!-- YouTube Video Section -->
    <section class="video-section">
      <div class="container">
        <h2 class="section-title">ION Fuzz Overdrive Demo</h2>
        <div class="video-container">
          <iframe 
            src="https://www.youtube.com/embed/2o_rCrXClX8" 
            title="UV Effects ION Fuzz Overdrive Demo"
            frameborder="0" 
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share" 
            allowfullscreen>
          </iframe>
        </div>
      </div>
    </section>

    <!-- Full Pedal Range Section -->
    <section class="pedal-range-section">
      <div class="container">
        <h2 class="section-title">Complete Pedal Range</h2>
        <div class="pedal-range-grid">
          <router-link to="/pedals/gluon/" class="range-pedal">
            <img src="@/assets/thumbnails/gluon1.jpg" alt="GLUON Fuzz - $95" />
            <h3>GLUON</h3>
            <p class="pedal-type">One-Knob Fuzz</p>
            <p class="pedal-price">$95</p>
          </router-link>

          <router-link to="/pedals/fermion/" class="range-pedal">
            <img src="@/assets/thumbnails/fermion1.jpg" alt="FERMION Overdrive - $95" />
            <h3>FERMION</h3>
            <p class="pedal-type">Transparent OD</p>
            <p class="pedal-price">$95</p>
          </router-link>

          <router-link to="/pedals/muon/" class="range-pedal">
            <img src="@/assets/thumbnails/muon1.jpg" alt="MUON Boost - $95" />
            <h3>MUON</h3>
            <p class="pedal-type">Dirty Boost</p>
            <p class="pedal-price">$95</p>
          </router-link>

          <router-link to="/pedals/pion/" class="range-pedal">
            <img src="@/assets/thumbnails/pion1.jpg" alt="PION Fuzz + Overdrive + Boost - $175" />
            <h3>PION</h3>
            <p class="pedal-type">Fuzz + OD + Boost</p>
            <p class="pedal-price">$175</p>
          </router-link>
        </div>
        <div class="browse-all-container">
          <router-link to="/shop/" class="browse-all-button">Shop All Pedals</router-link>
        </div>
        <p class="free-shipping-notice">FREE SHIPPING on all orders</p>
      </div>
    </section>

    <!-- FAQ Section -->
    <section class="faq-section">
      <div class="container">
        <h2 class="section-title">Frequently Asked Questions</h2>
        <div class="faq-grid" itemscope itemtype="https://schema.org/FAQPage">
          <div class="faq-item" itemscope itemprop="mainEntity" itemtype="https://schema.org/Question">
            <h3 itemprop="name">What are NOS components?</h3>
            <div itemscope itemprop="acceptedAnswer" itemtype="https://schema.org/Answer">
              <p itemprop="text">
                New Old Stock (NOS) components are unused vintage parts from the 1960s-1980s. We use NOS silicon and germanium transistors, diodes, and JFETs for their warm, musical character and dynamic response—qualities that modern components can't replicate. Each part is tested and selected for optimal tone.
              </p>
            </div>
          </div>

          <div class="faq-item" itemscope itemprop="mainEntity" itemtype="https://schema.org/Question">
            <h3 itemprop="name">Which pedal is right for me?</h3>
            <div itemscope itemprop="acceptedAnswer" itemtype="https://schema.org/Answer">
              <p itemprop="text">
                Start with GLUON (fuzz), FERMION (overdrive), or MUON (boost) for single-stage simplicity. Want more range? ION combines fuzz + overdrive, TAUON stacks overdrive + boost, and PION offers all three in one enclosure. Visit our <router-link to="/shop/">pedals page</router-link> to explore the full range.
              </p>
            </div>
          </div>

          <div class="faq-item" itemscope itemprop="mainEntity" itemtype="https://schema.org/Question">
            <h3 itemprop="name">How does shipping work?</h3>
            <div itemscope itemprop="acceptedAnswer" itemtype="https://schema.org/Answer">
              <p itemprop="text">
                Orders are processed quickly and shipped from Colorado. All pedals come with a 1-year warranty covering defects in parts or workmanship. For shipping details and returns, see individual product pages.
              </p>
            </div>
          </div>

          <div class="faq-item" itemscope itemprop="mainEntity" itemtype="https://schema.org/Question">
            <h3 itemprop="name">What makes your multi-drive pedals different?</h3>
            <div itemscope itemprop="acceptedAnswer" itemtype="https://schema.org/Answer">
              <p itemprop="text">
                Each gain stage is carefully voiced to work together as a cohesive unit, not just two circuits in one box. We optimize signal flow, gain staging, and frequency response so you can stack stages without losing clarity or touch sensitivity. Learn more on our <router-link to="/components/">components page</router-link>.
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>

  </div>
</template>

<script>
export default {
  name: 'HomeView'
}
</script>

<style scoped>
.container {
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 20px;
}

/* Hero Section */
.hero {
  background: #fff;
  padding: 20px 20px;
}

.hero-content {
  max-width: 1200px;
  margin: 0 auto;
  display: flex;
  align-items: center;
  gap: 60px;
}

.hero-text {
  flex: 1;
}

.hero-text h1 {
  font-size: 3rem;
  font-weight: 400;
  margin-bottom: 20px;
  color: var(--text-primary);
  line-height: 1.2;
}

.hero-text p {
  font-size: 1.2rem;
  line-height: 1.6;
  color: var(--text-secondary);
}

.hero-text .shop-button {
  margin-top: 25px;
  display: inline-block;
}

.shop-button {
  position: relative;
  padding: 15px 20px;
  border-radius: 5px;
  background: 
    linear-gradient(#fff, #fff) padding-box,
    linear-gradient(135deg, #667eea 0%, #764ba2 100%) border-box;
  border: 2px solid transparent;
  transition: all 0.3s ease;
  color: #667eea;
  text-decoration: none;
  display: inline-block;
}

.shop-button:hover {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%) border-box;
  color: #fff;
}

/* Remove pseudo-element styles as we're using background-origin */
.shop-button:before {
  content: none;
}

.hero-image {
  flex: 1;
}

.hero-image img {
  width: 100%;
  height: auto;
  border-radius: 8px;
}

.ion-pro-section {
  background: #fff;
}

@media (max-width: 768px) {
  .hero-content {
    flex-direction: column;
    text-align: center;
  }
  
  .hero-text h1 {
    font-size: 2rem;
  }
}

/* YouTube Video Section */
.video-section {
  padding: 80px 20px;
  background: #fff;
}

.video-section .section-title {
  text-align: center;
  font-size: 2.5rem;
  margin-bottom: 60px;
  color: var(--text-primary);
}

/* FAQ Section */
.faq-section {
  padding: 80px 20px;
  background: #fff;
}

.faq-grid {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 40px;
  max-width: 1000px;
  margin: 0 auto;
}

.faq-item {
  text-align: center;
  padding: 40px;
  border-radius: 10px;
  border: 1px solid #e1e1e1;
}

.faq-item h3 {
  font-size: 1.5rem;
  margin-bottom: 20px;
  color: var(--text-primary);
  font-weight: 400;
}

.faq-item p {
  color: var(--text-secondary);
  line-height: 1.6;
  font-size: 1.1rem;
}

.faq-item a {
  color: #667eea;
  text-decoration: none;
  font-weight: 500;
}

.faq-item a:hover {
  text-decoration: underline;
}

/* Featured Pedal Section */
.featured-pedal-section {
  padding: 80px 20px;
  background: #fff;
}

.featured-pedal-content {
  display: flex;
  align-items: center;
  gap: 60px;
  max-width: 1000px;
  margin: 0 auto;
}

.featured-pedal-image {
  flex: 0 0 400px;
}

.featured-pedal-image img {
  width: 100%;
  height: auto;
  border-radius: 12px;
}

.featured-pedal-info {
  flex: 1;
}

.featured-label {
  font-size: 0.9rem;
  text-transform: uppercase;
  letter-spacing: 2px;
  color: var(--text-primary);
  margin-bottom: 10px;
}

.featured-pedal-info h2 {
  font-size: 2.5rem;
  font-weight: 400;
  color: var(--text-primary);
  margin-bottom: 5px;
}

.featured-subtitle {
  font-size: 1.3rem;
  color: #667eea;
  margin-bottom: 20px;
}

.featured-description {
  font-size: 1.1rem;
  line-height: 1.7;
  color: var(--text-secondary);
  margin-bottom: 25px;
}

.featured-price {
  font-size: 1.8rem;
  font-weight: 600;
  color: #667eea;
  margin-bottom: 25px;
}

.featured-button {
  position: relative;
  display: inline-block;
  padding: 15px 40px;
  border-radius: 5px;
  background: 
    linear-gradient(#fff, #fff) padding-box,
    linear-gradient(135deg, #667eea 0%, #764ba2 100%) border-box;
  border: 2px solid transparent;
  transition: all 0.3s ease;
  color: #667eea;
  text-decoration: none;
  font-weight: 400;
  font-size: 1.1rem;
}

.featured-button:hover {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%) border-box;
  color: #fff;
}

.video-container {
  max-width: 800px;
  margin: 0 auto;
  position: relative;
  width: 100%;
  aspect-ratio: 16 / 9;
  border-radius: 10px;
  overflow: hidden;
}

.video-container iframe {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  border-radius: 10px;
}

/* Features Section */
.features {
  padding: 80px 20px;
  background: #fff;
}

.section-title {
  text-align: center;
  font-size: 2.5rem;
  margin-bottom: 60px;
  color: var(--text-primary);
  font-weight: 400;
}

.features-grid {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 40px;
  max-width: 1000px;
  margin: 0 auto;
}

.feature {
  text-align: center;
  padding: 40px;
  border-radius: 10px;
  border: 1px solid #e1e1e1;
}

.feature h3 {
  font-size: 1.5rem;
  margin-bottom: 20px;
  color: var(--text-primary);
  font-weight: 400;
}

.feature p {
  color: var(--text-secondary);
  line-height: 1.6;
  font-size: 1.1rem;
}

/* Product Showcase Sections */
.product-showcase {
  padding: 80px 20px;
}

.product-content {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 60px;
  align-items: center;
}

.product-content.reverse {
  direction: rtl;
}

.product-content.reverse > * {
  direction: ltr;
}

.product-text h2 {
  font-size: 2.2rem;
  margin-bottom: 20px;
  color: var(--text-primary);
  font-weight: 400;
}

.product-text p {
  color: var(--text-secondary);
  line-height: 1.6;
  margin-bottom: 30px;
  font-size: 1.1rem;
}

.product-image {
  display: flex;
  justify-content: center;
  align-items: center;
}

.product-image img {
  width: 100%;
  max-width: 400px;
  height: auto;
  border-radius: 10px;
}

/* Responsive Design */
@media (max-width: 768px) {
  .hero-text h1 {
    font-size: 2rem;
  }
  
  .featured-pedal-content {
    flex-direction: column;
    text-align: center;
  }
  
  .featured-pedal-image {
    flex: none;
    width: 100%;
    max-width: 350px;
  }
  
  .featured-pedal-info h2 {
    font-size: 2.5rem;
  }
  
  .product-content {
    grid-template-columns: 1fr;
    gap: 40px;
    text-align: center;
  }
  
  .features-grid,
  .faq-grid {
    grid-template-columns: 1fr;
  }
}

/* Pedal Range Section */
.pedal-range-section {
  padding: 80px 20px;
  background: #fff;
}

.pedal-range-grid {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 30px;
  max-width: 1400px;
  margin: 0 auto;
}

.range-pedal {
  text-decoration: none;
  text-align: center;
}

.range-pedal img {
  width: 100%;
  height: auto;
  border-radius: 8px;
  margin-bottom: 15px;
}

.range-pedal h3 {
  font-size: 1.3rem;
  font-weight: 700;
  color: var(--text-primary);
  margin-bottom: 5px;
}

.range-pedal .pedal-type {
  font-size: 0.95rem;
  color: #667eea;
  margin-bottom: 8px;
}

.range-pedal .pedal-price {
  font-size: 1.1rem;
  color: var(--text-primary);
  font-weight: 600;
}

.browse-all-container {
  text-align: center;
  margin-top: 50px;
}

.browse-all-button {
  position: relative;
  display: inline-block;
  padding: 15px 40px;
  border-radius: 8px;
  background: 
    linear-gradient(#fff, #fff) padding-box,
    linear-gradient(135deg, #667eea 0%, #764ba2 100%) border-box;
  border: 2px solid transparent;
  transition: all 0.3s ease;
  color: #667eea;
  text-decoration: none;
  font-weight: 600;
  font-size: 1.1rem;
}

.browse-all-button:hover {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%) border-box;
  color: #fff;
}

.free-shipping-notice {
  text-align: center;
  margin-top: 30px;
  font-size: 1.4rem;
  font-weight: 400;
  letter-spacing: 0.05em;
  color: var(--text-primary);
}

/* Keychain Section */
.keychain-section {
  padding: 80px 0;
  background: #fff;
}

.keychain-content {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 60px;
  align-items: center;
}

.keychain-image img {
  width: 100%;
  border-radius: 12px;
  box-shadow: 0 10px 30px rgba(0,0,0,0.1);
}

.keychain-label {
  font-size: 1rem;
  color: #28a745;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.1em;
  margin-bottom: 10px;
}

.keychain-info h2 {
  font-size: 2.5rem;
  font-weight: 400;
  color: var(--text-primary);
  margin-bottom: 20px;
}

.keychain-description {
  font-size: 1.1rem;
  line-height: 1.8;
  color: var(--text-secondary);
  margin-bottom: 25px;
}

.keychain-button {
  position: relative;
  display: inline-block;
  padding: 15px 40px;
  border-radius: 5px;
  background: 
    linear-gradient(#fff, #fff) padding-box,
    linear-gradient(135deg, #667eea 0%, #764ba2 100%) border-box;
  border: 2px solid transparent;
  transition: all 0.3s ease;
  color: #667eea;
  text-decoration: none;
  font-weight: 400;
  font-size: 1.1rem;
}

.keychain-button:hover {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%) border-box;
  color: #fff;
}

@media (max-width: 1200px) {
  .pedal-range-grid {
    grid-template-columns: repeat(2, 1fr);
  }
}

@media (max-width: 768px) {
  .pedal-range-grid {
    grid-template-columns: repeat(2, 1fr);
  }

  .keychain-content {
    grid-template-columns: 1fr;
    gap: 40px;
  }

  .keychain-info h2 {
    font-size: 2rem;
  }
}

@media (max-width: 480px) {
  .pedal-range-grid {
    grid-template-columns: 1fr;
  }

  .keychain-info h2 {
    font-size: 1.6rem;
  }
}
</style>
